import type { AppDispatch } from "@/features/redux/store";
import type { IFActivityDetail } from "@/api/apiRsp/IFActivityList";
import { detectClientEnvironment } from "@/features/clientEnv";
import { getCurrentInstallLink } from "@/utils/countryConfigUtils";
import { enqueue } from "./modalOrchestratorSlice";
import { getRegistrationInstallPromptEligibility } from "./installPromptPolicy";
import { hasPendingInviteWheelEntry } from "./inviteWheelEntry";

export type PopupResult<T> = { ok: true; value: T } | { ok: false };

// ---- 活动列表过滤（从 useActivityPopupList 提取为纯函数）----

/**
 * 过滤活动列表：
 * - isPop !== 1 的过滤掉
 * - popType 规则：登录弹窗(1)、返回首页(2)、未登录(4)
 * - 本地"今日不再弹出"判定
 * - 已首充的过滤首充活动(activityType=7)
 * - 已绑手机号的过滤绑手机活动(jumpTo="4")
 */
function filterActivityList(
  data: IFActivityDetail[] | null | undefined,
  userInfo: any,
  isLogin: boolean,
  loginEnter: boolean
): IFActivityDetail[] {
  if (!data?.length) return [];

  // 过滤规则（返回 true = 过滤掉）
  const popCheck = (e: any) => e && e.isPop !== 1;

  const popTypeCheck = (e: any) => {
    if (e.popType === "") return true; // 空 popType 过滤掉
    if (!isLogin) {
      return !e.popType?.includes("4");
    } else {
      // 返回首页
      if (
        e.popType?.includes("2") &&
        window.location.pathname.includes("home")
      ) {
        return false;
      }
      return !(e.popType?.includes("1") && loginEnter);
    }
  };

  const localCheck = (e: any) => {
    const key = `today-nopop-${isLogin ? userInfo?.userId : ""}-${e?.id}`;
    try {
      const item = localStorage.getItem(key);
      if (item) {
        const day = JSON.parse(item);
        if (day === new Date().toLocaleDateString()) {
          return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  };

  const firstRechargeCheck = (e: any) =>
    userInfo?.isFirstRecharge && e.activityType === 7;

  const bindPhoneCheck = (e: any) =>
    userInfo?.mobile?.length && e.jumpTo === "4";

  const rules = [
    popCheck,
    popTypeCheck,
    localCheck,
    firstRechargeCheck,
    bindPhoneCheck,
  ];

  return data
    .map((e) => {
      for (const rule of rules) {
        if (e && rule(e)) return undefined;
      }
      return e;
    })
    .filter(Boolean)
    .sort(
      (a, b) => (b as any).updateTime - (a as any).updateTime
    ) as IFActivityDetail[];
}

// ---- 安装提示条件检查 ----

function shouldShowInstallPrompt() {
  return getRegistrationInstallPromptEligibility(
    detectClientEnvironment(),
    getCurrentInstallLink()
  );
}

export const enqueuePostAuthPopupTasks = (
  dispatch: AppDispatch,
  applied: Set<string>,
  isRegister: boolean,
  userInfo: any,
  notices: PopupResult<any[]>,
  special: PopupResult<any>,
  activities: PopupResult<IFActivityDetail[]>
): void => {
  const apply = (key: string, ready: boolean, enqueueTasks: () => void) => {
    if (!ready || applied.has(key)) return;
    applied.add(key);
    enqueueTasks();
  };

  apply("invite_wheel_entry", true, () => {
    if (!hasPendingInviteWheelEntry()) return;
    dispatch(
      enqueue({
        id: "invite_wheel_entry",
        type: "invite_wheel_entry",
        priority: 35,
        dedupeKey: "invite_wheel_entry",
        sessionOnceKey: "invite_wheel_entry",
        payload: {},
      })
    );
  });

  apply("notices", notices.ok, () => {
    if (notices.ok && Array.isArray(notices.value) && notices.value.length) {
      dispatch(
        enqueue({
          id: "login_pop_queue",
          type: "login_pop_queue",
          priority: 40,
          dedupeKey: "login_pop_queue",
          sessionOnceKey: "login_pop_queue",
          payload: { items: notices.value },
        })
      );
    }
  });
  apply("registration", true, () => {
    if (!isRegister) return;
    const installCheck = shouldShowInstallPrompt();
    if (installCheck.show) {
      dispatch(
        enqueue({
          id: "install_after_auth_prompt",
          type: "install_prompt",
          priority: 30,
          dedupeKey: "install_after_auth_prompt",
          sessionOnceKey: "install_prompt_after_auth_shown",
          payload: { env: installCheck.env },
        })
      );
    }
  });
  apply("special", special.ok, () => {
    if (special.ok && special.value?.timeRechargeRebate?.rechargeAmount) {
      dispatch(
        enqueue({
          id: "first_deposit_popup",
          type: "first_deposit",
          priority: 20,
          dedupeKey: "first_deposit_popup",
          sessionOnceKey: "first_deposit_popup",
          payload: {},
        })
      );
    }
  });
  apply("activities", activities.ok, () => {
    const filtered = filterActivityList(
      activities.ok ? activities.value : [],
      userInfo,
      true,
      true
    );
    if (filtered.length) {
      dispatch(
        enqueue({
          id: "activity_popup",
          type: "activity_popup",
          priority: 20,
          dedupeKey: "activity_popup",
          sessionOnceKey: "activity_popup",
          payload: { popList: filtered },
        })
      );
    }
  });
  apply("invite", true, () => {
    dispatch(
      enqueue({
        id: "invite_popup",
        type: "invite_popup",
        priority: 20,
        dedupeKey: "invite_popup",
        sessionOnceKey: "invite_popup",
        payload: {},
      })
    );
  });
};
